import { Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import Screen from '@/karehero-app/explore/Screen';
import { RECONCILE } from '@/karehero-app/explore/benefits-calculator/BenefitsCalculator.content';
import {
  ConfirmPair,
  Note,
  ProposalRow,
} from '@/karehero-app/explore/benefits-calculator/Parts';

// Section G. The four ways a mode that proposes answers goes wrong. None of
// these is an edge case: every one of them follows directly from holding a
// persistent record and letting answers arrive from more than one place.
//
// Subject binding (G2) is the one to worry about most. A carer talking about
// themselves and about the person they care for in the same breath is the normal
// case, not the exception, and a fact filed against the wrong person poisons
// everything downstream of it.

// Two answers for the same question from different times. The system does not
// get to pick, because it cannot know which is current.
export const G1Conflict = () => (
  <Screen
    title={RECONCILE.conflict.title}
    footer={
      <Button ariaLabel={RECONCILE.conflict.keep} variant='link' isFullWidth>
        {RECONCILE.conflict.keep}
      </Button>
    }
  >
    <Stack spacing={2}>
      <Typography variant='body2' color='text.secondary'>
        {RECONCILE.conflict.body}
      </Typography>
      <Stack spacing={1}>
        <ProposalRow
          question='Told us in March'
          value={RECONCILE.conflict.options[0]}
        />
        <ProposalRow
          question='From your note, today'
          value={RECONCILE.conflict.options[1]}
        />
      </Stack>
    </Stack>
  </Screen>
);
G1Conflict.displayName = 'G1Conflict';

export const G2Subject = () => (
  <Screen
    title={RECONCILE.subject.title}
    footer={
      <ConfirmPair
        confirm={RECONCILE.subject.options[0]}
        change={RECONCILE.subject.options[1]}
      />
    }
  >
    <Note
      title='We are not sure whose this is'
      tone='warn'
      icon='user-group'
      body={RECONCILE.subject.body}
    />
  </Screen>
);
G2Subject.displayName = 'G2Subject';

// Staleness. The production Fact model already carries an expiry, so this is a
// surface for a field that exists rather than a new concept.
export const G3Stale = () => (
  <Screen
    title={RECONCILE.stale.title}
    subtitle={RECONCILE.stale.body}
    footer={
      <ConfirmPair confirm={RECONCILE.stale.yes} change={RECONCILE.stale.no} />
    }
  >
    <Stack spacing={1}>
      {RECONCILE.stale.rows.map((row) => (
        <ProposalRow
          key={row.question}
          question={row.question}
          value={row.value}
        />
      ))}
    </Stack>
  </Screen>
);
G3Stale.displayName = 'G3Stale';

// An answer arrives for a question early stopping already retired. Thrown away
// silently it looks like the note was ignored, so it gets said out loud once.
export const G4Retired = () => (
  <Screen
    title={RECONCILE.retired.title}
    footer={
      <Button ariaLabel={RECONCILE.retired.cta} isFullWidth>
        {RECONCILE.retired.cta}
      </Button>
    }
  >
    <Note title='Kept, not used' icon='check' body={RECONCILE.retired.body} />
  </Screen>
);
G4Retired.displayName = 'G4Retired';
